"use client"

import { useEffect, useState } from "react"
import { getAuth } from "firebase/auth"
import { getDatabase, ref, onValue } from "firebase/database"
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage"
import { Link } from "react-router-dom"
import Button from "./Button"
import "./LoanHistory.css"

export default function LoanHistory() {
  const [applications, setApplications] = useState([])
  const [selectedApp, setSelectedApp] = useState(null)
  const [documentUrls, setDocumentUrls] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [isListOpen, setIsListOpen] = useState(true) // Show list by default
  const auth = getAuth()
  const storage = getStorage()

  useEffect(() => {
    const user = auth.currentUser
    if (!user) {
      setError("You must be logged in to view your application history.")
      setLoading(false)
      return
    }

    const db = getDatabase()
    const applicationsRef = ref(db, `Finance/${user.uid}`)

    const unsubscribe = onValue(
      applicationsRef,
      (snapshot) => {
        const data = snapshot.val()
        if (data) {
          const applicationList = Object.entries(data).map(([applicationId, details]) => ({
            applicationId,
            ...details,
          }))
          setApplications(applicationList)
          setSelectedApp(null) // No auto-selection
        } else {
          setApplications([])
          setSelectedApp(null)
          setDocumentUrls({})
        }
        setLoading(false)
      },
      (error) => {
        setError("Failed to fetch application history: " + error.message)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [])

  // Fetch URLs for selected application
  useEffect(() => {
    if (!selectedApp || !auth.currentUser) return

    const fetchUrlsForApp = async () => {
      const urls = {}
      const docTypes = ["aadhar", "pan", "salary", "bank", "employment"]
      for (const docType of docTypes) {
        if (selectedApp.documents?.[docType] === "yes" && selectedApp.documentFileNames?.[docType]) {
          try {
            const fileRef = storageRef(storage, `Finance/${auth.currentUser.uid}/${selectedApp.documentFileNames[docType]}`)
            const url = await getDownloadURL(fileRef)
            urls[docType] = url
          } catch (err) {
            console.error(`Failed to fetch URL for ${docType} in ${selectedApp.applicationId}:`, err)
            urls[docType] = ""
          }
        } else {
          urls[docType] = ""
        }
      }
      setDocumentUrls((prev) => ({ ...prev, [selectedApp.applicationId]: urls }))
    }

    fetchUrlsForApp()
  }, [selectedApp])

  const handleSelectApp = (app) => {
    setSelectedApp(app)
    if (window.innerWidth <= 768) {
      setIsListOpen(false) // Hide list on mobile after selection
    }
  }

  const handleViewDocument = (url) => {
    if (url && url !== "") {
      window.open(url, "_blank") // Open valid URL
    }
  }

 if (loading) {
  return (
    <div className="lh-container">
      <div className="lh-spinner">
        <div className="lh-spinner-circle"></div>
      </div>
      
    </div>
  );
}

if (error) {
  return (
    <div className="lh-container">
      <p className="lh-error">{error}</p>
      <Link to="/dashboard/finance">
        <Button className="lh-button-primary">Back to Finance</Button>
      </Link>
    </div>
  );
}

  return (
    <div className="lh-page">
      <div className="lh-container">
        <Link to="/dashboard/finance">
          <Button className="lh-button-primary">Back to Finance</Button>
        </Link>
        <h2 className="lh-section-title">Loan Application History</h2>
        <p className="lh-section-subtitle">Select an application to view its details.</p>
        {applications.length === 0 ? (
          <p className="lh-no-applications">No loan applications found.</p>
        ) : (
          <div className="lh-content-wrapper">
            {/* Application List */}
            <div className={`lh-applications-list ${isListOpen ? 'lh-list-open' : ''}`}>
             
              <div className="lh-list-content">
                <h4>Applications</h4>
                {applications.map((app) => (
                  <div
                    key={app.applicationId}
                    className={`lh-application-item ${selectedApp?.applicationId === app.applicationId ? 'lh-selected' : ''}`}
                    onClick={() => handleSelectApp(app)}
                  >

                    <span className="lh-application-label">Application ID</span>
                    <h3 className="lh-application-id">{app.applicationId}</h3>
                    <p className="lh-timestamp">{app.timestamp}</p>
                  </div>
                ))}
              </div>
            </div>
            {/* Separator */}
            <div className="lh-separator"></div>
            {/* Details Panel */}
            <div className={`lh-details-panel ${selectedApp ? 'lh-details-visible' : ''}`}>
              {selectedApp ? (
                <>
                  <div className="lh-details-header">
                    <h3 className="lh-application-id">Application ID: {selectedApp.applicationId}</h3>
                    <p className="lh-timestamp">{selectedApp.status} | {selectedApp.timestamp}</p>
                  </div>
                  <div className="lh-details-content">
                    <div className="lh-details-left">
                      <div className="lh-card">
                        <h4>Personal Information</h4>
                        <p>
                          <i className="fas fa-user lh-icon"></i>
                          <strong>Name:</strong> {selectedApp.personalInfo.firstName} {selectedApp.personalInfo.lastName}
                        </p>
                        <p>
                          <i className="fas fa-envelope lh-icon"></i>
                          <strong>Email:</strong> {selectedApp.personalInfo.email}
                        </p>
                        <p>
                          <i className="fas fa-phone lh-icon"></i>
                          <strong>Phone:</strong> {selectedApp.personalInfo.phone}
                        </p>
                        <p>
                          <i className="fas fa-id-card lh-icon"></i>
                          <strong>PAN:</strong> {selectedApp.personalInfo.pan || "Not provided"}
                        </p>
                        <p>
                          <i className="fas fa-building lh-icon"></i>
                          <strong>GSTIN:</strong> {selectedApp.personalInfo.gstin || "Not provided"}
                        </p>
                        <p>
                          <i className="fas fa-birthday-cake lh-icon"></i>
                          <strong>Date of Birth:</strong> {selectedApp.personalInfo.dateOfBirth || "Not provided"}
                        </p>
                        <p>
                          <i className="fas fa-map-marker-alt lh-icon"></i>
                          <strong>Address:</strong> {selectedApp.personalInfo.address || "Not provided"}
                        </p>
                      </div>
                    </div>
                    <div className="lh-details-right">
                      <div className="lh-card">
                        <h4>Financial Details</h4>
                        <p>
                          <i className="fas fa-money-bill lh-icon"></i>
                          <strong>Loan Type:</strong> {selectedApp.financialDetails.loanType}
                        </p>
                        <p>
                          <i className="fas fa-rupee-sign lh-icon"></i>
                          <strong>Loan Amount:</strong> ₹{selectedApp.financialDetails.loanAmount}
                        </p>
                        <p>
                          <i className="fas fa-clock lh-icon"></i>
                          <strong>Tenure:</strong> {selectedApp.financialDetails.tenure}
                        </p>
                        <p>
                          <i className="fas fa-wallet lh-icon"></i>
                          <strong>Monthly Income:</strong> ₹{selectedApp.financialDetails.monthlyIncome}
                        </p>
                        <p>
                          <i className="fas fa-briefcase lh-icon"></i>
                          <strong>Employment Type:</strong> {selectedApp.financialDetails.employmentType}
                        </p>
                        <p>
                          <i className="fas fa-building lh-icon"></i>
                          <strong>Company:</strong> {selectedApp.financialDetails.company}
                        </p>
                      </div>
                      <div className="lh-card">
                        <h4>Documents</h4>
                        <p>
                          <i
                            className={`fas ${selectedApp.documents?.aadhar === "yes" ? "fa-check-circle lh-icon-present" : "fa-times-circle lh-icon-absent"}`}
                            aria-label="Aadhar document status"
                          ></i>
                          <span>Aadhar</span>
                          {selectedApp.documents?.aadhar === "yes" && (
                            <>
                              <button
                                onClick={() => handleViewDocument(documentUrls[selectedApp.applicationId]?.aadhar)}
                                className="lh-view-icon"
                                disabled={!documentUrls[selectedApp.applicationId]?.aadhar || documentUrls[selectedApp.applicationId].aadhar === ""}
                                aria-label="View Aadhar document"
                              >
                                
                              </button>
                              {!documentUrls[selectedApp.applicationId]?.aadhar || documentUrls[selectedApp.applicationId].aadhar === "" ? (
                                <span className="lh-url-unavailable"></span>
                              ) : null}
                            </>
                          )}
                        </p>
                        <p>
                          <i
                            className={`fas ${selectedApp.documents?.pan === "yes" ? "fa-check-circle lh-icon-present" : "fa-times-circle lh-icon-absent"}`}
                            aria-label="PAN document status"
                          ></i>
                          <span>PAN</span>
                          {selectedApp.documents?.pan === "yes" && (
                            <>
                              <button
                                onClick={() => handleViewDocument(documentUrls[selectedApp.applicationId]?.pan)}
                                className="lh-view-icon"
                                disabled={!documentUrls[selectedApp.applicationId]?.pan || documentUrls[selectedApp.applicationId].pan === ""}
                                aria-label="View PAN document"
                              >
                               
                              </button>
                              {!documentUrls[selectedApp.applicationId]?.pan || documentUrls[selectedApp.applicationId].pan === "" ? (
                                <span className="lh-url-unavailable"></span>
                              ) : null}
                            </>
                          )}
                        </p>
                        <p>
                          <i
                            className={`fas ${selectedApp.documents?.salary === "yes" ? "fa-check-circle lh-icon-present" : "fa-times-circle lh-icon-absent"}`}
                            aria-label="Salary document status"
                          ></i>
                          <span>Salary</span>
                          {selectedApp.documents?.salary === "yes" && (
                            <>
                              <button
                                onClick={() => handleViewDocument(documentUrls[selectedApp.applicationId]?.salary)}
                                className="lh-view-icon"
                                disabled={!documentUrls[selectedApp.applicationId]?.salary || documentUrls[selectedApp.applicationId].salary === ""}
                                aria-label="View Salary document"
                              >
                                
                              </button>
                              {!documentUrls[selectedApp.applicationId]?.salary || documentUrls[selectedApp.applicationId].salary === "" ? (
                                <span className="lh-url-unavailable"></span>
                              ) : null}
                            </>
                          )}
                        </p>
                        <p>
                          <i
                            className={`fas ${selectedApp.documents?.bank === "yes" ? "fa-check-circle lh-icon-present" : "fa-times-circle lh-icon-absent"}`}
                            aria-label="Bank document status"
                          ></i>
                          <span>Bank</span>
                          {selectedApp.documents?.bank === "yes" && (
                            <>
                              <button
                                onClick={() => handleViewDocument(documentUrls[selectedApp.applicationId]?.bank)}
                                className="lh-view-icon"
                                disabled={!documentUrls[selectedApp.applicationId]?.bank || documentUrls[selectedApp.applicationId].bank === ""}
                                aria-label="View Bank document"
                              >
                               
                              </button>
                              {!documentUrls[selectedApp.applicationId]?.bank || documentUrls[selectedApp.applicationId].bank === "" ? (
                                <span className="lh-url-unavailable"></span>
                              ) : null}
                            </>
                          )}
                        </p>
                        <p>
                          <i
                            className={`fas ${selectedApp.documents?.employment === "yes" ? "fa-check-circle lh-icon-present" : "fa-times-circle lh-icon-absent"}`}
                            aria-label="Employment document status"
                          ></i>
                          <span>Employment</span>
                          {selectedApp.documents?.employment === "yes" && (
                            <>
                              <button
                                onClick={() => handleViewDocument(documentUrls[selectedApp.applicationId]?.employment)}
                                className="lh-view-icon"
                                disabled={!documentUrls[selectedApp.applicationId]?.employment || documentUrls[selectedApp.applicationId].employment === ""}
                                aria-label="View Employment document"
                              >
                               
                              </button>
                              {!documentUrls[selectedApp.applicationId]?.employment || documentUrls[selectedApp.applicationId].employment === "" ? (
                                <span className="lh-url-unavailable"></span>
                              ) : null}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="lh-no-selection">
                  <p>Please select an application to view its details.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}